import type {
  TeamPlayerFinderFormValues,
  TeamPlayerFinderRegistration,
  TeamPlayerFinderUpdateBody,
} from '../types'

/** The card's editable state, as the server currently has it. */
export const toPlayerFinderFormValues = (
  entry: TeamPlayerFinderRegistration
): TeamPlayerFinderFormValues => ({
  recruiting: entry.recruiting,
  positionIds: entry.positions.map((position) => String(position.id)),
})

/**
 * May the switch change? Turning recruiting OFF is always allowed to a
 * registration that manages the ad (including a cancelled one); turning it ON
 * needs `can_opt_in`. A switch that is already on stays movable either way, so a
 * rep can flip it off and back before saving.
 */
export const canChangeRecruiting = (
  entry: TeamPlayerFinderRegistration
): boolean => entry.can_manage && (entry.recruiting || entry.can_opt_in)

/**
 * Positions are edited here only when the ad owns them. Listed Team Budget
 * slots own them otherwise (`positions_source === 'slots'`), one per slot.
 */
export const canChangePositions = (
  entry: TeamPlayerFinderRegistration
): boolean => entry.can_manage && entry.can_edit_positions

/**
 * R1 — a CANCELLED registration that still runs the team's ad. It may only turn
 * the ad off; the card offers nothing else.
 */
export const isWithdrawOnly = (entry: TeamPlayerFinderRegistration): boolean =>
  entry.can_manage &&
  entry.recruiting &&
  entry.opt_in_blockers.includes('cancelled')

/**
 * Slot-owned positions: the sheet's listed open slots set them, so the card
 * shows them read-only and points at Team Budget.
 */
export const positionsOwnedBySlots = (
  entry: TeamPlayerFinderRegistration
): boolean => entry.positions_source === 'slots'

const sameIds = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) {
    return false
  }
  const left = [...a].sort()
  const right = [...b].sort()

  return left.every((id, index) => id === right[index])
}

/**
 * The PUT body for what the rep changed, or null when nothing changed.
 *
 * Only CHANGED fields are sent, compared with the entry as the server has it
 * now — the endpoint is PATCH-like, so an absent key is left alone. A field the
 * registration may not change is never sent, whatever the form holds.
 *
 * `revision` is the revision the rep's edit is BASED on, never silently the
 * latest one: sending a fresher revision would let a stale edit overwrite a
 * change the rep never saw.
 *
 * Positions go out as numeric ids; none selected is `null` — any player.
 */
export const buildPlayerFinderUpdateBody = (
  entry: TeamPlayerFinderRegistration,
  values: TeamPlayerFinderFormValues,
  revision: string
): TeamPlayerFinderUpdateBody | null => {
  const body: TeamPlayerFinderUpdateBody = { revision }
  let changed = false

  if (canChangeRecruiting(entry) && values.recruiting !== entry.recruiting) {
    body.recruiting = values.recruiting
    changed = true
  }

  if (
    canChangePositions(entry) &&
    !sameIds(values.positionIds, toPlayerFinderFormValues(entry).positionIds)
  ) {
    body.positions =
      values.positionIds.length > 0 ? values.positionIds.map(Number) : null
    changed = true
  }

  return changed ? body : null
}

/** Whether Save has anything to send. */
export const hasPlayerFinderChanges = (
  entry: TeamPlayerFinderRegistration,
  values: TeamPlayerFinderFormValues
): boolean => buildPlayerFinderUpdateBody(entry, values, '') !== null

/**
 * After a 409: the form to show against the FRESH settings.
 *
 * A field the rep changed (compared with what their edit was based on) keeps the
 * rep's choice, so they can review it and save again. A field they did NOT touch
 * takes the fresh value — keeping the stale one would quietly revert whatever the
 * other editor changed the moment the rep saved.
 */
export const keepUnsavedChoices = (
  base: TeamPlayerFinderFormValues,
  current: TeamPlayerFinderFormValues,
  fresh: TeamPlayerFinderFormValues
): TeamPlayerFinderFormValues => ({
  recruiting:
    current.recruiting !== base.recruiting
      ? current.recruiting
      : fresh.recruiting,
  positionIds: sameIds(current.positionIds, base.positionIds)
    ? fresh.positionIds
    : current.positionIds,
})
